import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OktaAuthConfigService, OktaConfig } from '@okta/okta-angular';
import OktaAuth, { OktaAuthOptions } from '@okta/okta-auth-js';
import { catchError, map, Observable, ReplaySubject, take, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

interface TenantAuthConfigResponse {
  oktaClientId: string;
  oktaIssuer: string;
}

@Injectable({
  providedIn: 'root'
})
export class ClientService {

  private roleSubject = new ReplaySubject<{ [key: string]: boolean }>(1);
  role$: Observable<{ [key: string]: boolean }> = this.roleSubject.asObservable();

  private tenantNameSubject = new ReplaySubject<string>(1);
  tenantName$: Observable<string> = this.tenantNameSubject.asObservable();

  private userNameSubject = new ReplaySubject<string>(1);
  userName$: Observable<string> = this.userNameSubject.asObservable();

  private role: { [key: string]: boolean } = {};
  private tenantName: string = '';
  private userName: string = '';

  constructor(private http: HttpClient, 
    private handler: HttpBackend, 
    private configService: OktaAuthConfigService) { }

  getRole(): { [key: string]: boolean } {
    return this.role;
  }

  getTenantName(): string {
    return this.tenantName;
  }

  getUserName(): string {
    return this.userName;
  }

  setTenantName(name: string) {
    this.tenantName = name;
    this.tenantNameSubject.next(this.tenantName);
  }

  setSelectedTenantName(name: string | null | undefined) {
    this.setTenantName(name?.trim() ? name : '');
  }

  getPrivileges() {
    const url = `api/crfs-security/auth/current-user`;
    return this.http.get<any>(url).pipe(
      map(res => {
        this.role = {};
        res.roles.forEach((r: any) => {
          this.role[r] = true
        });
        this.roleSubject.next(this.role);
        this.setTenantName(this.shouldClearTenantName() ? '' : res.tenantName);
        this.userName = res.user?.userName || '';
        this.userNameSubject.next(this.userName);
        return res;
      })
    );
  }

  private shouldClearTenantName(): boolean {
    return Boolean(this.role['CRFS_CRS_ADMIN_ROLE'] || this.role['CRFS_CRS_SUPPORT_ROLE']);
  }

  private handleError(error: HttpErrorResponse) {
    window.alert("System Error : Unable to retrieve Client_ID, Please verify if the tenant configuration is correct")
    return throwError(() => new Error('Something bad happened; please try again later.'));
  }

  getClientId() {
    const url = `/api/crfs-security/auth/auth-clientid`;
    const httpHeaders = new HttpHeaders({
      'Accept': 'application/vnd.crs.crfs.security.v1+json',
      'Content-type': 'application/vnd.crs.crfs.security.v1+json'
    });
    const options = { headers: httpHeaders };
    return () =>
    new HttpClient(this.handler)
      .get<TenantAuthConfigResponse>(url, options)
      .pipe(
        map(({ oktaClientId, oktaIssuer }) => ({
            issuer: oktaIssuer,
            clientId: oktaClientId,
            redirectUri: environment.oidc.redirectUri
          })),
        catchError(this.handleError),
        tap((authConfig: OktaAuthOptions) => {
          const oktaAuth = new OktaAuth(authConfig);
          const moduleConfig: OktaConfig = { oktaAuth };
          this.configService.setConfig(moduleConfig);
        }),
        take(1)
      );
  }
}
